import {
  ActionTypeCreate,
  ActionTypeDelete,
  AuditEvent,
  ChangeTypeBlockNumber,
  ChangeTypeSetAll,
  ChangeTypeSetDisabled,
  ChangeTypeSetInbound,
  ChangeTypeSetOutbound,
  ChangeTypeUnblockNumber,
  ChangeTypeUpdateHoldMusic,
  HoldMusicLocation,
} from '@frontend/api-phone-audits';
import { formatDate } from '@frontend/date';
import { i18next } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';

const SUMMARY_SEPARATOR = ' • ';

/**
 * A mapping of change types to their corresponding summary formatting functions. Each function takes a change object
 * and returns a human-readable string summarizing the change.
 */
// TODO: change any type to a more specific type once we have the actual schema from the backend.
const changeSummaryFormatters: Record<string, (change: any) => string> = {
  [ChangeTypeBlockNumber]: (c) =>
    i18next.t('{{phoneNumber}} was blocked', {
      phoneNumber: formatPhoneNumber(c.blockNumber?.phoneNumber),
      ns: 'phone',
    }),
  [ChangeTypeUnblockNumber]: (c) =>
    i18next.t('{{phoneNumber}} was unblocked', {
      phoneNumber: formatPhoneNumber(c.blockNumber?.phoneNumber),
      ns: 'phone',
    }),
  [ChangeTypeSetInbound]: () => i18next.t('Call Recording enabled for incoming calls', { ns: 'phone' }),
  [ChangeTypeSetOutbound]: () => i18next.t('Call Recording enabled for outgoing calls', { ns: 'phone' }),
  [ChangeTypeSetAll]: () => i18next.t('Call Recording enabled for all calls', { ns: 'phone' }),
  [ChangeTypeSetDisabled]: () => i18next.t('Call Recording disabled', { ns: 'phone' }),
  [ChangeTypeUpdateHoldMusic]: (c) => {
    const locations = c.updateHoldMusic?.locations || [];
    if (locations.length === 0) return i18next.t('Hold Music was updated', { ns: 'phone' });

    return locations
      .map((loc: HoldMusicLocation) => {
        const musicName = loc.mediaItemName
          ? loc.mediaItemName.replace('.mp3', '')
          : i18next.t('Unknown', { ns: 'phone' });
        return loc.locationName
          ? // TODO: we need to handle the case for single location vs multiple locations. For now, we will just return
            // the location name for each location. If multiple locations, we return "Hold Music for {{locationName}}
            // was set to {{musicName}}". If single location, we can return "Hold Music was set to {{musicName}}".
            i18next.t('Hold Music for {{locationName}} was set to {{musicName}}', {
              locationName: loc.locationName,
              musicName,
              ns: 'phone',
            })
          : i18next.t('Hold Music was set to {{musicName}}', { musicName, ns: 'phone' });
      })
      .join(SUMMARY_SEPARATOR);
  },
};

/**
 * Main utility function to get summaries for an event. Traverses the changes in the event and uses the
 * changeSummaryFormatters to generate a summary for each change.
 * If no changes are present, it returns a default message indicating that no changes were recorded.
 *
 * @param event The audit event for which to generate summaries.
 * @returns An array of strings, each representing a summary of a change in the event.
 */
function getEventSummaries(event: AuditEvent): string[] {
  if (!event.changes || event.changes.length === 0) {
    return [i18next.t('No changes recorded', { ns: 'phone' })];
  }

  return event.changes.map((change) => {
    const formatter = changeSummaryFormatters[change.type || ''];
    return formatter
      ? formatter(change)
      : i18next.t('Updated {{objectName}}', { objectName: event.objectName || '', ns: 'phone' });
  });
}

/**
 * Returns a single string summarizing all changes in an event, separated by the SUMMARY_SEPARATOR.
 *
 * @param event The audit event for which to generate a summary string.
 * @returns A localized string summarizing all changes in the event.
 */
export function getEventSummaryString(event: AuditEvent): string {
  const summaries = getEventSummaries(event);
  return summaries.join(SUMMARY_SEPARATOR);
}

/**
 * Returns a human-readable title for an audit event card.
 * - UPDATE: "N update" / "N updates" based on the number of changes.
 * - CREATE: "Created {objectName}"
 * - DELETE: "Deleted {objectName}"
 *
 * @param event The audit event for which to generate a card title.
 * @returns A localized string suitable for use as the event card heading.
 */
export function getEventCardTitle(event: AuditEvent): string {
  const { action, objectName, changes } = event;

  if (action === ActionTypeCreate) {
    return i18next.t('Created {{objectName}}', { objectName: objectName ?? '', ns: 'phone' });
  }

  if (action === ActionTypeDelete) {
    return i18next.t('Deleted {{objectName}}', { objectName: objectName ?? '', ns: 'phone' });
  }

  // Default to update count (covers UPDATE action and any unknown action types)
  const count = changes?.length ?? 0;
  return i18next.t('{{count}} update', { count, ns: 'phone' });
}

/**
 * Formats a timestamp into a human-readable string.
 *
 * @param timestamp The timestamp to format.
 * @returns A localized string representing the formatted timestamp.
 */
export const formatEventTime = (timestamp: string | undefined): string =>
  timestamp ? formatDate(timestamp, 'MMM DD, YYYY h:mm:ss A z') : i18next.t('Unknown time', { ns: 'phone' });
